import type { OverlayPair } from "../lib/proposalOverlay";
import { useEffect, useMemo, useRef, useState } from "react";
import { mondayKey, shortDate } from "../lib/weekLine";
import { DeloadProposal } from "../components/DeloadProposal";
import { supabase } from "../lib/supabase";
import {
  removeAssignment, rescheduleAssignment, setAssignmentHidden, setDayPosition,
  type AssignmentWriter, type MenuItem,
} from "../lib/assignmentActions";
import { placeOnDay, type DropPlan } from "../lib/dayDrag";
import { describeOutcome, planWrites, summarise } from "../lib/assign";
import { tellTheClient } from "../lib/assignPush";
import { fetchAssignedWorkoutTree, treeSections } from "../lib/assignedWorkoutTree";
import { LIBRARY_ROWS_SELECT, readLibraryRows } from "../lib/libraryRows";
import { exerciseIdsOf, equipmentChips } from "../lib/phoneOverview";
import { printoutOf } from "../lib/workoutPrintout";
import { printWorkouts } from "../components/WorkoutPrintSheet";
import { countedForSet, mediaItem, type MediaItem } from "../lib/media";
import { clipsOfSet, orderClips, type SetClip } from "../lib/setClips";
import { ClipPlayButton } from "../components/ClipPlayer";
import { type Assignment } from "../lib/client";
import { TrainingCalendar } from "../components/TrainingCalendar";
import { ExerciseSetWindow } from "../components/ExerciseSetWindow";
import { writeWeekTrees, type WeekRpc } from "../lib/weekWrite";
import { AssignWorkoutWindow } from "../components/AssignWorkoutWindow";
import { href, hrefKeepingQuery, routeQuery } from "../lib/route";
import { useQuerySort } from "../lib/useQuerySort";
import { pickerDay, withPicker, withoutPicker } from "../lib/assignPicker";
import {
  checkReschedule,
  isDayKey,
  describeEdit,
  describeEditOutcome,
  describeRescheduleCheck,
  noteWrite,
  removalConsequence,
  visibilityChange,
  type EditableAssignment,
} from "../lib/assignmentEdit";
import {
  dayView,
  describePerformedSet,
  describeSetEffort,
  describePlannedSet,
  exerciseOutcome,
  reconcile,
  sessionMatchingPlan,
  type PerformedExercise,
  type PlannedExercise,
} from "../lib/training";

/* The Training tab — what you assigned, and what they actually did.
 *
 * `EVERFIT-MAP.md` §2b maps their version: a calendar with an **Assignment ⇄ History toggle**,
 * planned or performed, one switch. Its verdict is DISCARD the model and ADD two things, and
 * this is one of them — it is also the V7 review loop's own question
 * (`docs/V7-REVIEW-LOOP-SPEC.md`), which a shipping product answers with a switch.
 *
 * 🔴 THIS SHOWS THE TWO SIDES TOGETHER RATHER THAN BEHIND A TOGGLE, AND THAT IS A DECISION WITH
 * A REASON RATHER THAN A SUBSTITUTION. **They toggle because their model cannot do better**:
 * §2b measured that in their product the CALENDAR is the truth and a programme is a template
 * extracted afterwards, so a planned day and a performed day are two different objects that
 * happen to share a date. Ours are one — the assignment is a client-scoped copy (V7-2) and the
 * performed row carries the library id that the prescription names — so the two can be paired
 * per exercise and put side by side, which answers *"did they do what I asked"* in one look
 * instead of two and a memory. **The map's own instruction is to make it ours, not to copy it.**
 * The columns stack on a narrow screen, so nothing is lost where a toggle would have been used.
 *
 * ⚠️ AND WHERE THE PAIRING CANNOT BE TRUSTED IT IS NOT MADE. `training.ts` returns
 * `paired` / `notTrained` / `unpairable`, never a half-pairing — every row after a mismatch
 * would name the WRONG prescription and look entirely plausible.
 */

type Loaded = {
  planned: PlannedExercise[];
  performed: PerformedExercise[] | null;
  /** Every set logged that day, across ALL sessions — the number `client.ts` counts. */
  dayLoggedTotal: number;
};

type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | ({ status: "ready" } & Loaded);

export function ClientTraining(props: {
  clientId: string;
  /** QUEUE N2b-2 — the client's name, for the printed pages' corner. */
  clientName?: string;
  /** W72 step 2 — the proposals overlay, passed straight to the calendar (absent = the manual view). */
  overlay?: { pairs: readonly OverlayPair[]; counts: Readonly<Record<string, number>> };
  onOverlayWritten?: () => void;
  /** The overlay's own switch, drawn on the calendar bar's right (Fable, step 2 FIX 1). */
  overlayToggle?: { on: boolean; disabled: boolean; onToggle: (next: boolean) => void };
  assignments: Assignment[];
  assignmentWorkoutIds: Record<string, string>;
  unitSystem: string | null;
  trainingDays: Set<string>;
  today: Date;
  /**
   * Existing assignments keyed as the unique index keys them, for the duplicate prediction.
   *
   * ⚠️ THE `id` IS NEW AND IT IS WHAT LETS A ROW BE MOVED AT ALL. `checkReschedule` reads this
   * same list, and without an id the row being moved would collide with ITSELF on every day it
   * already occupies — so every reschedule would be predicted as a duplicate and refused before
   * it was attempted.
   */
  existingSources: { id: string; scheduledDateISO: string | null; sourceWorkoutId: string | null }[];
  /** Re-read the client after a write, so the day list and the counters agree with the server. */
  onChanged: () => void;
  /** Job 3 step 5 — a new task on a day (the page's `TaskEditor`), handed to the ONE calendar. Absent = a non-active link. */
  onAddTask?: (dayKey: string) => void;
  /** Job 3 step 5 cut 2 — this week's empty sentence, drawn by the calendar under this week's row. Absent = not empty. */
  emptyWeekLine?: string;
}) {
  const { clientId, assignments, assignmentWorkoutIds, unitSystem, trainingDays, today,
          existingSources, onChanged, onAddTask } = props;

  /* Newest first: a coach reviewing looks at the most recent day, not the first one ever.
   *
   * 🔴 A HIDDEN ASSIGNMENT IS LISTED, AND UNTIL SESSION 11 IT WAS NOT. This filter read
   * `!a.hiddenFromClient`, so the two hidden rows in his own account were invisible on the one
   * screen that shows a day — which was survivable while nothing here could hide anything, and
   * becomes a trap the moment a Hide control exists: the row would vanish on the click with no
   * way back to it, which is the dead-control shape this workstream has refused three times.
   * A hidden day is marked `notShown` and stays reachable.
   *
   * ⚠️ AN UNDATED ASSIGNMENT IS STILL EXCLUDED, AND FOR A REASON RATHER THAN BY INHERITANCE.
   * This panel pairs a plan against ONE DAY's logged work, and a row with no `scheduled_date`
   * has no day to pair against. There are zero of them live; the phone can create one (assigning
   * with no days picked is the assessment case), so `undated` earns its place in the outcome
   * vocabulary — it just has nothing to render here. That is also why the reschedule control
   * below moves a day and never clears it.
   */
  const days = useMemo(
    () =>
      [...assignments]
        .filter((a) => a.scheduledDateISO !== null)
        .sort((a, b) => (b.scheduledDateISO ?? "").localeCompare(a.scheduledDateISO ?? "")),
    [assignments],
  );

  /* 🔴 W63 — NO DAY IS OPEN UNTIL THE COACH OPENS ONE, from the card's `⋯` (View · Move… · Note… ·
     Remove…). It defaulted to the most recent past day because a panel under the calendar always
     showed one; that panel and the radio wall that drove it are gone, and a window that opened on
     its own would be a window nobody asked for. */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dayWindow, setDayWindow] = useState<"view" | "reschedule" | "note" | "remove" | "copy" | null>(null);
  function closeDay(): void { setDayWindow(null); setSelectedId(null); }

  const selected = days.find((a) => a.id === selectedId) ?? null;

  /* W22(a) — what the calendar's `⋯` does. `hide` is reversible and its label says exactly what
     pressing it will do, so it acts; the other two hand over to the day's own controls.
     ⚠️ FOCUS FOLLOWS THE SELECTION. Setting state and leaving the pointer where it was would
     move the panel under a coach who is looking at a grid two hundred pixels above it — and a
     keyboard user would be standing on a trigger that has just closed. The radio is focused on
     the next frame, once React has drawn the new selection. */
  const [menuSaid, setMenuSaid] = useState<string | null>(null);
  /* W22(b) — which exercise the floating editor is open on. `null` is closed, and the PAIR is
     held together rather than as two states: a workout id with no row id is a window that cannot
     decide what it is editing. */
  const [editing, setEditing] =
    useState<{ workoutId: string; exerciseRowId: string } | null>(null);
  /* 🔴 W63 — THE DAY THE PICKER IS OPEN FOR LIVES IN THE ADDRESS, as theirs does
     (`?view=find-a-workout&date=…`, §8a-ter). Read through `useQuerySort`, which listens to
     `hashchange`: these screens do not remount on navigation, and a value read once in an
     initialiser is the bug DRIVEN on 2026-09-18. Opening is a push (Back closes it); closing is a
     navigation that drops `view` and keeps `date`, so the calendar stays on that week. */
  const [pickerFor] = useQuerySort<string | null>(pickerDay, routeQuery);
  function openPicker(day: string): void {
    window.location.hash = `${href({ name: "client", clientId })}${withPicker(routeQuery(window.location.hash), day)}`.slice(1);
  }
  function closePicker(): void {
    window.location.hash = `${href({ name: "client", clientId })}${withoutPicker(routeQuery(window.location.hash))}`.slice(1);
  }
  function onMenu(id: string, item: MenuItem): void {
    /* QUEUE N2b-2 — `Print workout`: this ONE workout as Everfit's grid. Its tree and its exercises' library rows are read on
       the click (the same two reads the overview window makes), then the browser's own print dialog. Nothing is written. */
    if (item.kind === "print") {
      const workoutId = assignmentWorkoutIds[id];
      const day = assignments.find((a) => a.id === id)?.scheduledDateISO ?? null;
      if (workoutId === undefined) return;
      void (async () => {
        const r = await fetchAssignedWorkoutTree((fn, args) => supabase.rpc(fn, args), workoutId);
        if (r.tree === null) { setMenuSaid(r.error); return; }
        const lib = await readLibraryRows(
          (part) => supabase.from("coach_exercises").select(LIBRARY_ROWS_SELECT).in("id", part), exerciseIdsOf(r.tree));
        printWorkouts([printoutOf(r.tree, {
          client: props.clientName ?? "", dateISO: day, fields: lib.fields,
          equipment: equipmentChips(r.tree, lib.equipment).map((c) => c.label),
        })]);
      })();
      return;
    }
    /* William's item 4 — the card's pencil: the builder on THIS day's copy, over this page, on this page's query. */
    if (item.kind === "edit") {
      const workoutId = assignmentWorkoutIds[id];
      if (workoutId === undefined) return;
      window.location.hash = hrefKeepingQuery({ name: "clientWorkout", clientId, workoutId }, window.location.hash).slice(1);
      return;
    }
    if (item.kind === "hide") {
      void (async () => {
        const r = await setAssignmentHidden(supabase as unknown as AssignmentWriter, id, item.hidden);
        setMenuSaid(r.said);
        onChanged();
      })();
      return;
    }
    setSelectedId(id);
    setMenuSaid(null);
    setDayWindow(item.kind);
  }
  /* 🔴 W137 cut 2 (R6, R7) — WHAT A DROP ON THE WEEK MEANS. A new day is a RESCHEDULE through the same check and the
     same write `Move…` uses (`checkReschedule` first — the one-source-per-day refusal says its existing sentence and the
     card goes home — then `rescheduleAssignment`, whose 23505 is described too). Then each row whose order on its day
     changed gets its `day_position`. The calendar draws the drop at once (optimistic) and puts it back on a refusal; the
     page re-reads either way so a half-landed move (moved, order refused) shows the truth. */
  async function moveWorkout(plan: DropPlan): Promise<boolean> {
    const db = supabase as unknown as AssignmentWriter;
    const a = assignments.find((x) => x.id === plan.id);
    if (a === undefined) return false;
    let said: string | null = null;
    if (plan.moved !== null) {
      const check = checkReschedule({
        id: a.id, scheduledDateISO: a.scheduledDateISO, hiddenFromClient: a.hiddenFromClient, note: a.note,
        workoutName: a.workoutName, sourceWorkoutId: existingSources.find((e) => e.id === a.id)?.sourceWorkoutId ?? null,
      }, plan.moved.to, existingSources);
      if (check.kind !== "free") { setMenuSaid(describeRescheduleCheck(check)); return false; }
      const r = await rescheduleAssignment(db, a.id, check.dateISO, shortDate(check.dateISO));
      if (!r.ok) { setMenuSaid(r.said); return false; }
      said = r.said;
    }
    for (const p of plan.positions) {
      const r = await setDayPosition(db, p.id, p.position);
      if (!r.ok) { setMenuSaid(r.said); onChanged(); return false; }
    }
    setMenuSaid(said);
    onChanged();
    return true;
  }
  const [state, setState] = useState<State>({ status: "idle" });
  const heading = useRef<HTMLHeadingElement>(null);

  /* ⚠️ THE SELECTED DAY CAN STOP EXISTING, AND ONLY ONE CONTROL ON THIS PAGE CAN DO THAT.
   * Removing an assignment leaves `selectedId` naming a row the parent's refetch has dropped, so
   * `selected` goes null and the panel renders nothing at all — which reads as the screen
   * breaking rather than as the removal succeeding. Re-defaulting to the newest remaining day is
   * what a coach expects; the guard is `selectedId !== null` so this can never fight the initial
   * choice, and never re-selects after a coach deliberately has nothing selected. */
  useEffect(() => {
    /* W63: the day is shown in a WINDOW now, so a day that stopped existing closes it — re-choosing
       the newest remaining day (the old rule) would put a different day under the coach's eyes
       inside the window they opened for this one. */
    if (selectedId !== null && !days.some((a) => a.id === selectedId)) {
      setSelectedId(null);
      setDayWindow(null);
    }
  }, [days, selectedId]);

  useEffect(() => {
    if (selected === null || selected.scheduledDateISO === null) {
      setState({ status: "idle" });
      return;
    }
    const workoutId = assignmentWorkoutIds[selected.id];
    if (workoutId === undefined) {
      setState({ status: "error", message: "This assignment names no workout." });
      return;
    }
    let cancelled = false;
    const date = selected.scheduledDateISO;
    setState({ status: "loading" });

    async function load(workoutId: string, date: string) {
      // THE PLANNED SIDE. ⚠️ The sets come from `coach_workout_sets` and NEVER from the
      // per-exercise summary columns — V6-8 measured that the builder reads only the set rows
      // and that `sets`/`reps_min`/`hold_seconds_*` are legacy, so reading them would produce a
      // screen that looks right in the database and disagrees with what the coach authored.
      // THROUGH THE ONE DOOR (`assigned_workout_tree`, migration 20260906120000): one visibility
      // check on the server, then the whole tree — the read that paid a policy per row before.
      // The generated `database.types.ts` carries the function since the 2026-09-06 push, so the
      // call is typed end to end — the two `as never` casts that bridged the gap are gone.
      const plan = await fetchAssignedWorkoutTree(
        (fn, args) => supabase.rpc(fn, args),
        workoutId,
      );

      // THE PERFORMED SIDE. Every session on that date, because a day is not one session:
      // 2026-08-27 carries SIXTEEN rows for one client and TWO of them hold work.
      const done = await supabase
        .from("workout_sessions")
        // THE MEDIA rides on the exercise instance (`form_clips.workout_session_exercise_id`): the
        // rows RLS returns are exactly the ones this coach may see (migration 20260905120000) —
        // an athlete with no active coach, or a past client, contributes nothing here.
        .select("id, workout_session_exercises(position, coach_exercise_id, set_logs(set_number, reps_completed, weight_kg, hold_seconds, rpe, distance_m, rounds), form_clips(id, set_number, media_kind, storage_path, recorded_at, position, counted_reps))")
        .eq("user_id", clientId)
        .eq("scheduled_date", date);

      if (plan.tree === null) {
        if (!cancelled) setState({ status: "error", message: plan.error });
        return;
      }
      if (done.error) {
        if (!cancelled) setState({ status: "error", message: done.error.message });
        return;
      }

      const planned = flattenPlan(treeSections(plan.tree));
      // W136 B2 S3 — a run's unit is the coach FIELD's: the door's exercise object carries only `name`, so the coach's own
      // library rows are read for `tracking_fields` (best-effort: a failed read leaves metres, never a failed page).
      const ids = [...new Set(planned.map((p) => p.libraryExerciseId).filter((x): x is string => x !== null))];
      if (ids.length > 0) {
        const lib = await supabase.from("coach_exercises").select("id, tracking_fields").in("id", ids);
        const km = new Set(((lib.data ?? []) as { id: string; tracking_fields: string[] | null }[])
          .filter((r) => (r.tracking_fields ?? []).includes("distance_km")).map((r) => r.id));
        for (const p of planned) if (p.libraryExerciseId !== null && km.has(p.libraryExerciseId)) p.distanceKm = true;
      }
      const sessions = (done.data ?? []).map((s) => ({
        id: s.id,
        exercises: (s.workout_session_exercises ?? []).map((e) => ({
          ordinal: e.position ?? 0,
          libraryExerciseId: e.coach_exercise_id,
          // In SET order: a PostgREST embed arrives unordered (the codex), and W116 hangs each set's clips on its own line.
          loggedSets: [...(e.set_logs ?? [])].sort((a, b) => (a.set_number ?? 0) - (b.set_number ?? 0)).map((l) => ({
            setNumber: l.set_number,
            reps: l.reps_completed,
            weightKg: l.weight_kg === null ? null : Number(l.weight_kg),
            holdSeconds: l.hold_seconds,
            distanceM: l.distance_m === null || l.distance_m === undefined ? null : Number(l.distance_m),
            rounds: l.rounds ?? null,
            rpe: l.rpe === null ? null : Number(l.rpe),
            // W127: the stored band label is not read — no band is printed under a set any more (his word, 2026-10-01).
          })),
          media: (e.form_clips ?? []).map(mediaItem).filter((m): m is MediaItem => m !== null),
        })),
      }));

      // The DAY's total, across every session — never the chosen one's, which is lossy.
      let dayLoggedTotal = 0;
      for (const s of sessions) for (const e of s.exercises) dayLoggedTotal += e.loggedSets.length;

      const match = sessionMatchingPlan(sessions, planned);
      if (!cancelled) {
        setState({
          status: "ready",
          planned,
          performed: match?.exercises ?? (sessions.length === 0 ? null : sessions[0]?.exercises ?? null),
          dayLoggedTotal,
        });
      }
    }

    void load(workoutId, date);
    return () => {
      cancelled = true;
    };
  }, [selected?.id, selected?.scheduledDateISO, clientId, assignmentWorkoutIds]);

  const view = state.status === "ready" ? dayView(state.planned, state.performed) : null;
  const accountedFor =
    view?.kind === "paired"
      ? view.rows.reduce((n, r) => n + (r.performed?.loggedSets.length ?? 0), 0)
      : 0;
  const gap = state.status === "ready" && view?.kind === "paired"
    ? reconcile(state.dayLoggedTotal, accountedFor)
    : null;

  return (
    <section aria-labelledby="training-h" className="training">
      {/* JOB 3 · WILLIAM (2026-10-15): the caps `TRAINING` under the `Week` tab said the tab's name again — kept for the
          screen reader and as the focus target, hidden from the eye. */}
      <h2 id="training-h" ref={heading} tabIndex={-1} className="ht-visually-hidden client-section-heading">
        Training
      </h2>
      {/* 🔴 THE CALENDAR IS THE SURFACE NOW, AND THE TABLE STAYS UNDER IT. §2b-bis measured
          theirs and a coach's week is a grid, not a list — but the list is what carries the
          hidden-assignment label, the note and the reschedule controls, and deleting it to make
          the copy tidy would take four working things away to gain a layout. Grid first because
          that is what the eye wants; list below because that is where the work is done. */}
      {/* 🔴 W22(a) — THE DAY CARD'S `⋯` IS WIRED, AND UNTIL NOW IT HAD NO CALLER AT ALL.
          `TrainingCalendar` has taken `onAssignmentAction` since W11b and this screen never
          passed it, so the menu the component, its own module and two test files all describe
          **was never drawn once**. A prop with no caller is the dead-branch shape this repo's
          codex names; it was found by rendering the calendar to check the new weekday row.
          ⚠️ AND ONLY `Hide` ACTS FROM HERE. `Reschedule…` and `Remove` SELECT the day in the
          list below instead, because that is where `removalConsequence` prints how many logged
          sets a removal would destroy and where the date control lives — a popover cannot carry
          either, and a second copy of them is exactly what `assignmentActions` exists to stop.
          The ellipsis on `Reschedule…` already promises a further step; `Remove` gets one too,
          which is more honest than a popover that deletes on one click. */}
      {/* F15 4.2 — a deload the logged numbers call for, one tap (absent when none is open). */}
      <DeloadProposal clientId={clientId} thisMonday={mondayKey(today)} onChanged={props.onChanged} />
      <TrainingCalendar
        clientId={clientId}
        clientName={props.clientName}
        overlay={props.overlay}
        onOverlayWritten={props.onOverlayWritten}
        overlayToggle={props.overlayToggle}
        onAddTask={onAddTask}
        /* Job 3 step 5 — a task removed in a day's tasks window re-reads the client, as every other write here does. */
        onTasksChanged={onChanged}
        emptyWeekLine={props.emptyWeekLine}
        assignments={assignments}
        trainingDays={trainingDays}
        today={today}
        onAssignmentAction={(id, item) => { onMenu(id, item); }}
        /* W22(f) — the EXPANDED cell's tiles need the tree's id, and this page already holds the
           map (it is the same read the list below uses). */
        workoutIds={assignmentWorkoutIds}
        /* W22(b) — a tile click opens the per-exercise editor. The prop is OPTIONAL on the
           calendar and passing it is what makes the tiles controls at all; without a caller they
           are spans, which is the W22(g) lesson applied rather than quoted. */
        onEditExercise={(workoutId, exerciseRowId) => { setEditing({ workoutId, exerciseRowId }); }}
        /* 🔴 W63 — THE DAY'S DISC OPENS THEIR "ASSIGN A WORKOUT" WINDOW FOR THAT DAY (§8a-ter:
           the cell's `+` disc, then `Select` — two steps). Until W63 it ticked a checkbox in a
           form under the calendar and focused a `<select>` there: five steps and a scroll,
           measured in `~/ht-logs/w63/before/`. The day is answered by the press; the window asks
           only which workout. */
        onAddTo={(day) => { openPicker(day); }}
        /* W137 cut 2 — the 4-arrow drag: another day, or above/below another workout on a day. */
        onMoveWorkout={moveWorkout}
        /* Week editor stage 1 (2026-10-19) — the Extended week edits in place through ONE write (`write_week_trees`, SECURITY
           INVOKER: the coach's own RLS decides every row). A copy created or dropped re-reads the client. */
        onWriteWeek={(payloads) => writeWeekTrees(supabase.rpc.bind(supabase) as unknown as WeekRpc, payloads)}
        onWeekStructureChanged={onChanged}
        /* Week editor stage 2 — the library panel's Workouts tab: a template dropped (or chosen) on a day is assigned there by
           the SAME steps as the Assign window — the duplicate check, `assign_coach_workout` (copy-on-assign), the outcome
           reader, the push to the client, the page's re-read. */
        onAssignWorkout={async (w, day, index) => {
          if (planWrites([day], w.rootId, existingSources)[0]?.action === "alreadyAssigned") {
            return { ok: false, said: `${shortDate(day)} already has ${w.name}.` };
          }
          const r = await supabase.rpc("assign_coach_workout", { p_workout_id: w.id, p_client_id: clientId, p_scheduled_date: day, p_note: "" });
          const outcome = describeOutcome(day, r.error);
          if (outcome.kind === "assigned") {
            tellTheClient(r.data ?? null, 1);
            /* Its place among the day's workouts — the 4-arrow drag's own write (`setDayPosition`), row by row. */
            const onDay = assignments.filter((a) => a.scheduledDateISO === day && !a.isTask)
              .sort((a, b) => (a.dayPosition ?? 0) - (b.dayPosition ?? 0) || (a.assignedAtISO ?? "").localeCompare(b.assignedAtISO ?? ""));
            const newId = typeof r.data === "string" ? r.data : null;
            if (newId !== null && onDay.length > 0) {
              const stored = Object.fromEntries(onDay.map((a) => [a.id, a.dayPosition ?? 0]));
              for (const p of placeOnDay(onDay.map((a) => a.id), newId, index, stored)) {
                const m = await setDayPosition(supabase as unknown as AssignmentWriter, p.id, p.position);
                if (!m.ok) { onChanged(); return { ok: false, said: `${w.name} assigned to ${shortDate(day)}, but not in that place: ${m.said}` }; }
              }
            }
            onChanged();
            return { ok: true, said: `${w.name} assigned to ${shortDate(day)}.` };
          }
          return { ok: false, said: outcome.kind === "alreadyAssigned" ? `${shortDate(day)} already has ${w.name}.` : summarise([outcome]) };
        }}
      />

      {/* 🔴 W22(b) — THE FLOATING PER-EXERCISE EDITOR. It WRITES to a client's training, and the
          write was rehearsed per identity and rolled back before the component existed
          (`~/ht-logs/day-0919/web-dig4/w22b/rehearse-set-edit.sql`, six verdicts, two broken
          controls). No policy moved: an assigned copy is a `coach_workouts` row this coach owns,
          and `coach_workout_sets_owner_all` already grants the owner ALL.
          ⚠️ `onSaved` BUMPS THE PAGE'S OWN RELOAD KEY, because the calendar behind it cannot know
          a set moved — and a window that saves while the tile behind it still shows the old dose
          is how a coach comes to distrust the screen. */}
      {editing !== null && (
        <ExerciseSetWindow
          workoutId={editing.workoutId}
          exerciseRowId={editing.exerciseRowId}
          onClose={() => { setEditing(null); }}
          onSaved={onChanged}
        />
      )}

      {/* What the calendar's menu said, once, in the page's own voice. `aria-live` so a coach
          who acted from a popover that then closed still hears the outcome. */}
      {menuSaid !== null && (
        <p className="ht-callout training-menu-said" role="status">{menuSaid}</p>
      )}

      {pickerFor !== null && (
        <AssignWorkoutWindow
          key={pickerFor}
          clientId={clientId}
          day={pickerFor}
          existing={existingSources}
          today={today}
          onClose={closePicker}
          onAssigned={(said) => { setMenuSaid(said); closePicker(); onChanged(); }}
          /* William's item 4 — `Build a workout`: the builder, EMPTY, for this client and day; the picker's `view` goes. */
          buildHref={hrefKeepingQuery({ name: "clientWorkoutNew", clientId, day: pickerFor }, window.location.hash)}
        />
      )}

      {days.length === 0 && (
        <p className="ht-callout client-empty">You have not assigned anything yet.</p>
      )}

      {/* 🔴 W63 — ONE DAY, IN A WINDOW, OPENED FROM ITS CARD'S `⋯`. Replaces the radio wall (one pill
          per assignment — 60+ on his own client) and the "This day" panel under it. The controls
          and the planned-vs-performed review are the SAME components; only where they are reached
          moved. A trained day is read-only (Note only) — Fable's bound, `menuItems`' rule. */}
      {/* 🔴 W137 R6b — THE CARD'S COPY ICON: the same day window, in its copy mode (`Copy to…`). */}
      {selected !== null && dayWindow === "copy" && (
        <DayWindow title="Copy to…" dateISO={selected.scheduledDateISO} onClose={closeDay}>
          <CopyControls
            name={selected.workoutName ?? "Untitled workout"}
            clientId={clientId}
            copyWorkoutId={assignmentWorkoutIds[selected.id] ?? null}
            sourceWorkoutId={existingSources.find((e) => e.id === selected.id)?.sourceWorkoutId ?? null}
            fromISO={selected.scheduledDateISO}
            existing={existingSources}
            onCopied={(said) => { closeDay(); setMenuSaid(said); onChanged(); }}
          />
        </DayWindow>
      )}
      {selected !== null && dayWindow !== null && dayWindow !== "copy" && (
        <DayWindow
          title={selected.workoutName ?? "Untitled workout"}
          dateISO={selected.scheduledDateISO}
          onClose={closeDay}
        >
          {selected !== null && (
            <DayControls
              assignment={{
                id: selected.id,
                scheduledDateISO: selected.scheduledDateISO,
                sourceWorkoutId:
                  existingSources.find((e) => e.id === selected.id)?.sourceWorkoutId ?? null,
                hiddenFromClient: selected.hiddenFromClient,
                note: selected.note,
                workoutName: selected.workoutName,
              }}
              existing={existingSources}
              /* ⚠️ THE COUNT IS THE DAY'S TOTAL ACROSS EVERY SESSION, NEVER THE PAIRED ONE.
                 `removalConsequence` says what a removal destroys, and understating the logged
                 work would understate it — 2026-08-27 holds 50 sets across two sessions while
                 the session matching the plan holds 46. It is 0 while the day is still loading,
                 which is why the control waits rather than asking on an unknown. */
              loggedSetsThatDay={state.status === "ready" ? state.dayLoggedTotal : null}
              alreadyTrained={
                selected.scheduledDateISO !== null && trainingDays.has(selected.scheduledDateISO)
              }
              onChanged={onChanged}
              trained={selected.scheduledDateISO !== null && trainingDays.has(selected.scheduledDateISO)}
              focusOn={dayWindow}
              onRemoved={() => {
                closeDay();
                onChanged();
                // Focus would otherwise land on `<body>` — the button that had it no longer
                // exists. `b4-2`'s ruler 6 and session 10's measured `<dialog>` finding: a
                // browser guarantee you have read about is a claim like any other.
                heading.current?.focus();
              }}
            />
          )}

          {state.status === "loading" && (
            <p className="ht-callout" aria-live="polite">Loading that day…</p>
          )}
          {state.status === "error" && (
            <div role="alert" className="client-error">
              <p className="ht-body">Could not load that day.</p>
              <p className="ht-callout">{state.message}</p>
            </div>
          )}

          {view?.kind === "notTrained" && (
            <p className="ht-callout training-note">
              Nothing was recorded for this day. {view.planned.length} exercise
              {view.planned.length === 1 ? "" : "s"} assigned.
            </p>
          )}

          {view?.kind === "unpairable" && (
            <p className="ht-callout training-note">
              {/* ⚠️ NOT AN ERROR AND NOT AN EMPTY TABLE. Saying which way the two sides differ is
                  the whole value; inventing a row-by-row pairing here would name the wrong
                  prescription for every row after the first mismatch. */}
              What was recorded cannot be lined up against what you assigned — {view.reason}. The
              assigned workout is below; the recorded work is counted in the day totals.
            </p>
          )}

          {(view?.kind === "paired" || view?.kind === "unpairable" || view?.kind === "notTrained") && (
            <table className="training-table">
              <caption className="ht-visually-hidden">
                The assigned workout beside what was recorded
              </caption>
              <thead>
                <tr>
                  <th scope="col" className="ht-overline">Exercise</th>
                  <th scope="col" className="ht-overline">Assigned</th>
                  <th scope="col" className="ht-overline">Recorded</th>
                </tr>
              </thead>
              <tbody>
                {(view.kind === "paired"
                  ? view.rows
                  : view.planned.map((p) => ({ planned: p, performed: null }))
                ).map((row) => {
                  const o = exerciseOutcome(row.planned, row.performed);
                  return (
                    <tr key={row.planned.ordinal} className={`training-row-${o}`}>
                      <th scope="row" className="training-exercise">
                        <span className="ht-body-medium">{row.planned.name ?? "Untitled exercise"}</span>
                        {row.planned.sectionName !== null && (
                          <span className="ht-caption training-section">{row.planned.sectionName}</span>
                        )}
                        {row.planned.note !== null && row.planned.note !== "" && (
                          <span className="ht-caption training-note-cell">{row.planned.note}</span>
                        )}
                      </th>
                      <td className="ht-data-small training-sets" data-label="Assigned">
                        {row.planned.sets.map((s, i) => (
                          <span key={i} className="training-set">{describePlannedSet(s, unitSystem, row.planned.distanceKm === true)}</span>
                        ))}
                      </td>
                      <td className="ht-data-small training-sets" data-label="Recorded">
                        {/* 🔴 FOUND BY RENDERING, AND IT PRE-DATES THIS SESSION. This read
                            `view.kind !== "paired"`, so a day where NOTHING was recorded printed
                            "not lined up" in every cell — the `unpairable` sentence, which claims
                            the recorded work could not be matched against the plan. On a day with
                            no recorded work there is nothing to line up, and the banner directly
                            above already says so. One phrase standing for two different answers,
                            and the wrong one of the two. */}
                        {view.kind === "unpairable" ? (
                          <span className="ht-caption training-absent">not lined up</span>
                        ) : row.performed === null || row.performed.loggedSets.length === 0 ? (
                          <span className="ht-caption training-absent">nothing recorded</span>
                        ) : (
                          row.performed.loggedSets.map((s, i) => {
                            /* The reps in reserve stored on the set, on its own span so the set keeps
                               the planned column's grammar. W127 (his word, 2026-10-01): no band label
                               is printed here any more — the band is in the exercise's note. */
                            const effort = describeSetEffort(s);
                            const clips = asClips(row.performed?.media);
                            return (
                              <span key={i} className="training-set">
                                {describePerformedSet(s, unitSystem, row.planned.distanceKm === true)}
                                {effort !== null && <span className="ht-caption training-effort"> {effort}</span>}
                                {/* W116 — the set's own clips behind the ONE play button, on its line. */}
                                {s.setNumber != null && (
                                  <ClipPlayButton clips={clipsOfSet(clips, s.setNumber)}
                                                  setWords={`${row.planned.name ?? "Exercise"}, set ${String(s.setNumber)}`}
                                                  setLabel={String(s.setNumber)} />
                                )}
                                {/* F5: the camera's count is DATA, after the button. */}
                                {s.setNumber != null && countedForSet(row.performed?.media ?? [], s.setNumber) !== null && (
                                  <span className="ht-caption training-effort"> · {countedForSet(row.performed?.media ?? [], s.setNumber)} counted</span>
                                )}
                              </span>
                            );
                          })
                        )}
                        {/* W116 — a clip filed on a set no logged line holds (or on a day logged without numbers) is never
                            hidden for lack of a line: ONE button for them, under the sets. */}
                        {view.kind !== "unpairable" && row.performed !== null && (() => {
                          const logged = new Set(row.performed.loggedSets.map((s) => s.setNumber).filter((n): n is number => n != null));
                          const loose = orderClips(asClips(row.performed.media).filter((c) => c.setNumber === null || !logged.has(c.setNumber)));
                          return loose.length > 0 && (
                            <span className="training-set">
                              <ClipPlayButton clips={loose} setWords={row.planned.name ?? "Exercise"} setLabel={null} />
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {gap !== null && (
            // 🔴 MEASURED, 2026-08-27: the day holds 50 logged sets across two sessions while the
            // session matching the plan holds 46. A table showing 46 under a counter saying 50 is
            // a screen the coach cannot trust, so the difference is stated rather than smoothed.
            <p className="ht-caption training-note">{gap}</p>
          )}
        </DayWindow>
      )}
    </section>
  );
}

/* 🔴 W63 — THE DAY WINDOW. Our one window mechanism: a native `<dialog>` opened with `showModal`
   (the dim, the focus trap and Escape come from the browser), Escape and Close both ask the
   parent to close. It is NOT addressed: a day's controls are reached from the calendar in one
   click and hold no state worth a link — `backdropRoute`'s own test for what earns an address. */
/**
 * 🔴 W137 R6b — COPY THIS DAY'S WORKOUT TO ANOTHER DAY OF THIS CLIENT. His word (2026-10-02 ~11:45): *"we need a copy icon and
 * a 4 arrow one next to each workout title"*; Fable: it puts the same workout on another day.
 * THE WRITE IS THE EXISTING ASSIGN (`assign_coach_workout`, copy-on-assign) WITH THIS DAY'S CLIENT COPY AS THE SOURCE — never
 * the library template, which would reset a progressed week. The source's root rides along (the clone keeps it), so the
 * one-source-per-day index refuses the same workout twice on a day, and `planWrites` predicts that refusal before the call
 * with the page's existing sentence. Rehearsed rolled back per identity before it was wired
 * (`tools/w137-week-extended/rehearse-copy.sh`: coach accepted onto a free day, the same source again refused 23505, a
 * stranger and the client refused).
 */
function CopyControls({ name, clientId, copyWorkoutId, sourceWorkoutId, fromISO, existing, onCopied }: {
  name: string;
  clientId: string;
  copyWorkoutId: string | null;
  sourceWorkoutId: string | null;
  fromISO: string | null;
  existing: { id: string; scheduledDateISO: string | null; sourceWorkoutId: string | null }[];
  onCopied: (said: string) => void;
}) {
  const [day, setDay] = useState(fromISO ?? "");
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { requestAnimationFrame(() => { input.current?.focus(); }); }, []);
  async function copy() {
    if (copyWorkoutId === null || sourceWorkoutId === null) { setSaid("This assignment names no workout."); return; }
    if (!isDayKey(day)) { setSaid(describeRescheduleCheck({ kind: "notADate" })); return; }
    if (planWrites([day], sourceWorkoutId, existing)[0]?.action === "alreadyAssigned") {
      setSaid(describeRescheduleCheck({ kind: "collides" }));
      return;
    }
    setBusy(true);
    setSaid(null);
    const r = await supabase.rpc("assign_coach_workout", {
      p_workout_id: copyWorkoutId, p_client_id: clientId, p_scheduled_date: day, p_note: "",
    });
    setBusy(false);
    const outcome = describeOutcome(day, r.error);
    if (outcome.kind === "assigned") { tellTheClient(r.data ?? null, 1); onCopied(`${name} assigned to ${shortDate(day)}.`); return; }
    setSaid(outcome.kind === "alreadyAssigned" ? describeRescheduleCheck({ kind: "collides" }) : summarise([outcome]));
  }
  return (
    <section className="day-controls" aria-label="Copy to another day">
      <div className="day-control">
        <label className="ht-field-label" htmlFor="day-copy">Copy to</label>
        <input ref={input} id="day-copy" type="date" className="ht-input day-date" value={day}
               onChange={(e) => setDay(e.target.value)} />
        <button className="ht-button" onClick={() => void copy()} disabled={busy}>
          {busy ? "Copying…" : "Copy"}
        </button>
        {said !== null && <span className="ht-caption day-hint" role="status">{said}</span>}
      </div>
    </section>
  );
}

function DayWindow({ title, dateISO, onClose, children }: {
  title: string;
  dateISO: string | null;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el !== null && !el.open) el.showModal();
  }, []);
  return (
    <dialog ref={ref} className="ht-window day-window" aria-labelledby="day-window-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="ht-window-frame">
        <header className="ht-window-head">
          <div className="ht-window-titles">
            {dateISO !== null && <p className="page-eyebrow">{shortDate(dateISO)}</p>}
            <h2 className="ht-title" id="day-window-title">{title}</h2>
          </div>
          <button type="button" className="ht-button ht-button-quiet ht-window-close"
                  onClick={onClose}>Close</button>
        </header>
        <div className="ht-window-body day-window-body">{children}</div>
      </div>
    </dialog>
  );
}

/* W116 — a set's media as the ONE player's clips (the Training tab's day window plays through `ClipPlayButton`, the
 * review's own button; the signing is the stage's, on open — this page signs nothing). The pick's `position` rides along,
 * so a set's photos keep the order they were picked in (`media.orderMedia`'s rule, now `setClips.orderClips`'). */
function asClips(media: readonly MediaItem[] | undefined): SetClip[] {
  return (media ?? []).map((m) => ({ id: m.id, storagePath: m.storagePath, mediaKind: m.kind, setNumber: m.setNumber, recordedAtISO: m.recordedAt, position: m.position }));
}

/** PostgREST's nested rows → the flat ordered list `training.ts` pairs on. */
type PlanSectionRow = {
  position: number | null;
  name: string | null;
  coach_workout_exercises: {
    position: number | null;
    exercise_id: string | null;
    notes: string | null;
    /** F15 4.1b — a row a rule tab PAUSED (the door emits it to the coach); never shown to or logged by the client. */
    hidden?: boolean | null;
    coach_exercises: { name: string | null } | { name: string | null }[] | null;
    coach_workout_sets: {
      position: number | null;
      reps: number | null;
      weight_kg: number | string | null;
      time_seconds: number | null;
      rest_seconds: number | null;
      set_type: string | null;
      /** W136 B2 S3 — the door emits both (`distance_m` numeric — a string over some serialisers). */
      rounds?: number | null;
      distance_m?: number | string | null;
    }[] | null;
  }[] | null;
};

/**
 * Flatten by (section.position, exercise.position), which is the ordering the pairing depends on.
 *
 * ⚠️ THIS IS THE ORDER THAT WAS VERIFIED AGAINST REAL LOGGED DATA — 20 of 20 on 2026-08-11, a day
 * carrying eight duplicated library ids. Changing the sort here silently breaks every pairing on
 * the page while every row still renders.
 */
export function flattenPlan(sections: PlanSectionRow[]): PlannedExercise[] {
  const rows: PlannedExercise[] = [];
  const ordered = [...sections].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  for (const sec of ordered) {
    const exercises = [...(sec.coach_workout_exercises ?? [])].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0),
    );
    for (const ex of exercises) {
      // F15 4.1b — a PAUSED row is not part of the day the client trained, so it is not paired (nor drawn as planned).
      if (ex.hidden === true) continue;
      const embed = ex.coach_exercises;
      const name = embed === null ? null : Array.isArray(embed) ? (embed[0]?.name ?? null) : embed.name;
      const sets = [...(ex.coach_workout_sets ?? [])]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((s) => ({
          reps: s.reps,
          weightKg: s.weight_kg === null ? null : Number(s.weight_kg),
          timeSeconds: s.time_seconds,
          restSeconds: s.rest_seconds,
          setType: s.set_type,
          distanceM: s.distance_m === null || s.distance_m === undefined ? null : Number(s.distance_m),
          rounds: s.rounds ?? null,
        }));
      rows.push({
        ordinal: rows.length + 1,
        libraryExerciseId: ex.exercise_id,
        name,
        sectionName: sec.name,
        sets,
        note: ex.notes,
      });
    }
  }
  return rows;
}


/* ── THE DAY'S OWN CONTROLS ─────────────────────────────────────────────────────────────────
 *
 * `docs/WEB-APP-PLAN.md` §12.2 item 3, and `EVERFIT-MAP.md` §32.4's tier-1 #1 and #2 merged:
 * *the client page cannot DO anything* and *an assignment cannot be changed once made*. Before
 * this the whole web app could perform FOUR writes and none of them touched a `coach_assignments`
 * row after it was created — a coach who assigned the wrong day had to pick up their phone.
 *
 * ⚠️ INLINE, NOT A WINDOW, AND THAT IS A DECISION. Session 10 built a reusable window mechanism
 * (`backdropRoute` + a native `<dialog>`) and the temptation is to reach for it because it is
 * there. These controls act on the day already selected on this screen, next to the evidence the
 * coach is deciding from; putting them behind a backdrop would hide the planned-beside-performed
 * table at the exact moment it is the reason for the decision.
 *
 * ⚠️ EVERY WRITE NAMES ONE COLUMN. PostgREST updates only the columns a payload carries, which is
 * V6-2's finding stated for a screen: *a write says which columns it is authoritative about*. A
 * combined `update({ scheduled_date, note, hidden_from_client })` would rewrite two fields the
 * coach did not touch every time they moved a day.
 */
function DayControls(props: {
  assignment: EditableAssignment;
  existing: { id: string; scheduledDateISO: string | null; sourceWorkoutId: string | null }[];
  /** Null while the day is still being read — NOT zero. See the removal block below. */
  loggedSetsThatDay: number | null;
  alreadyTrained: boolean;
  /** W63 — a TRAINED day is a record: its window offers the note alone (Fable's bound). */
  trained: boolean;
  /** W63 — which menu item opened the window, so the control it named has the focus. */
  focusOn: "view" | "reschedule" | "note" | "remove";
  onChanged: () => void;
  onRemoved: () => void;
}) {
  const { assignment, existing, loggedSetsThatDay, alreadyTrained, trained, focusOn, onChanged, onRemoved } = props;

  const [dateDraft, setDateDraft] = useState(assignment.scheduledDateISO ?? "");
  const [noteDraft, setNoteDraft] = useState(assignment.note ?? "");
  // `Remove…` opens the window ALREADY ASKING: the consequence is shown and "Keep it" has the focus.
  const [arming, setArming] = useState(focusOn === "remove");
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  /* ⚠️ EVERY DRAFT IS RESET WHEN THE SELECTED DAY CHANGES, AND THE ARMED REMOVAL WITH IT.
   * These are one component reused across days rather than one per day, so without this a note
   * typed against Monday would still be sitting in the box after clicking Thursday — and, far
   * worse, a removal armed on one day would still be armed on the next, one click from deleting
   * a day the coach never looked at. */
  useEffect(() => {
    setDateDraft(assignment.scheduledDateISO ?? "");
    setNoteDraft(assignment.note ?? "");
    setArming(focusOn === "remove");
    setResult(null);
    // 🔴 KEYED ON THE ROW'S IDENTITY, NEVER ON ITS VALUES — AND THE FIRST DRAFT WAS KEYED ON
    // BOTH, WHICH MADE EVERY SUCCESSFUL WRITE SWALLOW ITS OWN CONFIRMATION. A save calls
    // `onChanged()`, the parent refetches, `assignment.note` (or `scheduledDateISO`) comes back
    // changed, this effect fires, and `setResult(null)` erases the "Note saved." the coach had
    // just earned. Found by WALKING it: the note reached the database, came back into the box,
    // and the screen said nothing at all.
    //
    // ⚠️ THE TELL WAS THE INCONSISTENCY. `hiddenFromClient` was not in the list, so hiding kept
    // its message while moving and noting lost theirs — three writes, two of them silently
    // swallowing their own result and one not.
  }, [assignment.id]);

  /* W63 — the control the menu named takes the focus once the window is drawn (and, for Remove,
     once the consequence has been read — "Keep it" does not exist before). `View` leaves the
     browser's own choice alone. */
  const ready = loggedSetsThatDay !== null;
  useEffect(() => {
    const id = focusOn === "reschedule" ? "day-move" : focusOn === "note" ? "day-note"
      : focusOn === "remove" ? "day-keep" : null;
    if (id !== null) requestAnimationFrame(() => { document.getElementById(id)?.focus(); });
  }, [focusOn, assignment.id, ready]);

  const check = checkReschedule(assignment, dateDraft, existing);
  const checkHint = describeRescheduleCheck(check);
  const visibility = visibilityChange(assignment, alreadyTrained);
  const pending = noteWrite(assignment.note, noteDraft);

  async function move() {
    if (check.kind !== "free") {
      setResult(checkHint);
      return;
    }
    setBusy("move");
    setResult(null);
    /* W11b — THROUGH THE SHARED MODULE. The calendar's `⋯` menu performs the same three actions,
       and two surfaces writing the same rows through two copies of the same statements is the
       drift this repo keeps paying for. The rules (`checkReschedule`, `describeEditOutcome`) were
       already shared; the WRITES were not, until now. */
    const r = await rescheduleAssignment(
      supabase as unknown as AssignmentWriter, assignment.id, check.dateISO,
      shortDate(check.dateISO));
    setBusy(null);
    setResult(r.said);
    if (r.ok) onChanged();
  }

  async function setVisibility() {
    setBusy("visibility");
    setResult(null);
    const r = await setAssignmentHidden(
      supabase as unknown as AssignmentWriter, assignment.id, visibility.next);
    setBusy(null);
    setResult(r.said);
    if (r.ok) onChanged();
  }

  async function saveNote() {
    if (pending.kind === "unchanged") {
      setResult("The note has not changed.");
      return;
    }
    setBusy("note");
    setResult(null);
    const { error } = await supabase
      .from("coach_assignments")
      .update({ note: pending.value })
      .eq("id", assignment.id);
    setBusy(null);
    setResult(describeEdit(describeEditOutcome(error), {
      done: pending.value === null ? "Note cleared." : "Note saved.",
      verb: "save the note",
    }));
    if (error === null) onChanged();
  }

  async function remove() {
    setBusy("remove");
    setResult(null);
    const r = await removeAssignment(supabase as unknown as AssignmentWriter, assignment.id);
    setBusy(null);
    setArming(false);
    if (!r.ok) { setResult(r.said); return; }
    onRemoved();
  }

  const consequence =
    loggedSetsThatDay === null ? null : removalConsequence(assignment, loggedSetsThatDay);

  return (
    <section className="day-controls" aria-labelledby="day-controls-h">
      <h3 id="day-controls-h" className="ht-overline">
        This day
      </h3>

      {!trained && (<>
      <div className="day-control">
        <label className="ht-field-label" htmlFor="day-move">Move to</label>
        {/* ⚠️ A NATIVE DATE INPUT, AND IT IS STRUCTURAL RATHER THAN CONVENIENT. Its value is a
            `YYYY-MM-DD` STRING — exactly what `coach_assignments.scheduled_date` is, a Postgres
            `date` with no time and no zone. So no `Date` is ever constructed on this path and
            the whole class of bug `CoachAssignmentService` documents at length ("pick Friday,
            store Thursday, and the coach's week is silently wrong") is unreachable rather than
            avoided by care. It also brings the calendar popup, keyboard entry and the reader's
            own locale with it. */}
        <input
          id="day-move"
          type="date"
          className="ht-input day-date"
          value={dateDraft}
          onChange={(e) => setDateDraft(e.target.value)}
        />
        {/* Enabled unless a request is in flight — §4a's verdict, held to across this app: a
            greyed-out button cannot say WHY it is greyed out, so pressing this one names what is
            wrong instead. */}
        <button className="ht-button" onClick={() => void move()} disabled={busy !== null}>
          {busy === "move" ? "Moving…" : "Move"}
        </button>
        {/* ⚠️ THE `unchanged` SENTENCE IS NOT RENDERED HERE, AND THE RULE STILL HAS ONE.
            The box is pre-filled with the day the assignment is on — which is what a coach needs
            to see before moving it — so `checkReschedule` answers `unchanged` on arrival and the
            panel opened reading "It is already on that day." under an untouched control. A hint
            that is present before anybody has done anything reads as an error and teaches the
            coach to ignore that line. The sentence still exists and is still tested; pressing
            Move without changing the day is what surfaces it, as the result. */}
        {checkHint !== null && check.kind !== "unchanged" && (
          <span className="ht-caption day-hint">{checkHint}</span>
        )}
      </div>

      <div className="day-control">
        <button className="ht-button" onClick={() => void setVisibility()} disabled={busy !== null}>
          {busy === "visibility" ? "Saving…" : visibility.label}
        </button>
        <span className="ht-caption day-hint">{visibility.consequence}</span>
      </div>
      </>)}

      <div className="day-control">
        <label className="ht-field-label" htmlFor="day-note">Note</label>
        {/* 🔴 THE FIRST READER THIS COLUMN HAS EVER HAD. `coach_assignments.note` has two writers
            and rendered nowhere — see `assignmentEdit.ts`. It is the COACH's note today; the
            athlete still cannot see it, which is why `AssignPanel`'s label no longer says
            otherwise. */}
        <input
          id="day-note"
          className="ht-input day-note"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          /* 🔴 W63 — THIS PLACEHOLDER PROMISED PRIVACY THE DATABASE DOES NOT KEEP. MEASURED
             2026-09-19 (the comment that used to sit on the assign form): the client can read
             `coach_assignments.note` over PostgREST. The field says what is true. */
          placeholder="Your client can read this"
        />
        <button className="ht-button" onClick={() => void saveNote()} disabled={busy !== null}>
          {busy === "note" ? "Saving…" : "Save note"}
        </button>
      </div>

      {!trained && (
      <div className="day-control day-control-remove">
        {!arming ? (
          <button className="ht-button" onClick={() => setArming(true)} disabled={busy !== null}>
            Remove this day
          </button>
        ) : consequence === null ? (
          /* ⚠️ AN UNKNOWN IS NOT A ZERO. While the day is still loading, `loggedSetsThatDay` is
             null, and passing that off as 0 would print "Nothing was logged for this day" over a
             day that may hold sixty sets — session 10's `items: []` finding, where one value
             stood for two different answers. The confirmation is simply not offered yet. */
          <p className="ht-callout day-hint" aria-live="polite">
            Still reading what was logged for this day — the confirmation will say what is at stake.
          </p>
        ) : (
          <div role="group" aria-labelledby="day-remove-h" className="day-confirm">
            <p id="day-remove-h" className="ht-body-medium">{consequence.headline}</p>
            <p className="ht-callout">{consequence.detail}</p>
            <button id="day-keep" className="ht-button" onClick={() => setArming(false)} disabled={busy !== null}>
              Keep it
            </button>
            <button className="ht-button" onClick={() => void remove()} disabled={busy !== null}>
              {busy === "remove" ? "Removing…" : "Remove"}
            </button>
          </div>
        )}
      </div>
      )}

      {result !== null && (
        <p className="ht-callout day-result" role="status" aria-live="polite">{result}</p>
      )}
    </section>
  );
}
